import * as Tone from 'tone';

export class Voice {
  constructor(output) {
    // Osc 1
    this.osc1 = new Tone.Oscillator({ type: 'sawtooth', frequency: 440 }).start();
    this.osc1Gain = new Tone.Gain(0.5);

    // Osc 2
    this.osc2 = new Tone.Oscillator({ type: 'square', frequency: 440 }).start();
    this.osc2Gain = new Tone.Gain(0.5);

    // FM modulation gain (osc2 modulates osc1 frequency)
    this.fmGain = new Tone.Gain(0);

    // Amplitude envelope
    this.ampEnv = new Tone.AmplitudeEnvelope({
      attack: 0.01,
      decay: 0.3,
      sustain: 0.6,
      release: 0.5,
    });

    // Mix node
    this.mixGain = new Tone.Gain(1);

    // Normal mode: both oscillators mix into amp envelope
    this.osc1.connect(this.osc1Gain);
    this.osc2.connect(this.osc2Gain);
    this.osc1Gain.connect(this.ampEnv);
    this.osc2Gain.connect(this.ampEnv);
    this.ampEnv.connect(this.mixGain);
    this.mixGain.connect(output);

    // FM mode: osc2 signal goes through fmGain into osc1's frequency
    this.osc2.connect(this.fmGain);
    this.fmGain.connect(this.osc1.frequency);

    this.active = false;
    this.note = null;
    this.fmEnabled = false;
  }

  noteOn(note, velocity = 1) {
    const freq = Tone.Frequency(note).toFrequency();
    this.osc1.frequency.setValueAtTime(freq, Tone.now());
    this.osc2.frequency.setValueAtTime(freq + this._osc2Detune(freq), Tone.now());
    this.ampEnv.triggerAttack(Tone.now(), velocity);
    this.active = true;
    this.note = note;
  }

  noteOff() {
    this.ampEnv.triggerRelease(Tone.now());
    this.active = false;
    this.note = null;
  }

  _osc2Detune(baseFreq) {
    // Convert cents to frequency offset
    const detuneCents = this._detuneCents || 0;
    return baseFreq * (Math.pow(2, detuneCents / 1200) - 1);
  }

  setOsc1Type(type) {
    this.osc1.type = type;
  }

  setOsc2Type(type) {
    this.osc2.type = type;
  }

  setDetune(cents) {
    this._detuneCents = cents;
  }

  setMix(mix) {
    if (this.fmEnabled) {
      this.osc1Gain.gain.setValueAtTime(1, Tone.now());
      this.osc2Gain.gain.setValueAtTime(0, Tone.now());
    } else {
      this.osc1Gain.gain.setValueAtTime(1 - mix, Tone.now());
      this.osc2Gain.gain.setValueAtTime(mix, Tone.now());
    }
  }

  setFM(enabled, amount) {
    this.fmEnabled = enabled;
    if (enabled) {
      this.fmGain.gain.setValueAtTime(amount, Tone.now());
      this.osc2Gain.gain.setValueAtTime(0, Tone.now());
      this.osc1Gain.gain.setValueAtTime(1, Tone.now());
    } else {
      this.fmGain.gain.setValueAtTime(0, Tone.now());
    }
  }

  setAmpEnv({ attack, decay, sustain, release }) {
    if (attack !== undefined) this.ampEnv.attack = attack;
    if (decay !== undefined) this.ampEnv.decay = decay;
    if (sustain !== undefined) this.ampEnv.sustain = sustain;
    if (release !== undefined) this.ampEnv.release = release;
  }

  dispose() {
    this.osc1.stop().dispose();
    this.osc2.stop().dispose();
    this.osc1Gain.dispose();
    this.osc2Gain.dispose();
    this.fmGain.dispose();
    this.ampEnv.dispose();
    this.mixGain.dispose();
  }
}
